// Setup gallery (ns-gallery; 05 "Gallery · by variant", parts/Overlays A2): chips filter the setups by a product in them and
// by desk size, a menu sorts them, and ?product=<handle> (from a product page or an email) preselects a product. Counts and
// sizes come from the setups on the page (a setup's desk_size text, e.g. "55 × 28 in"); a setup has no color or frame field,
// so there is no such filter. The Shop the look panels (inline and in the A2 popups, which the header opens with
// data-ns-dialog-open) keep their selected count, total and Add N to cart in step with their checkboxes; the add itself is
// the cart's (ns-cart.js takes over the /cart/add form). Every word is written with textContent.
const HANDLE = /^[a-z0-9][a-z0-9-]{0,254}$/;

export const fill = (template, values) => String(template ?? '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? String(values[key]) : match));

export function filterFromSearch(search) {
  const value = new URLSearchParams(search ?? '').get('product') ?? '';
  return HANDLE.test(value) ? value : '';
}

export const shows = (handles, filter) => filter === '' || String(handles ?? '').split(/\s+/).includes(filter);

// A click on the dialog element itself is its padding or the backdrop; only a point outside its box is the backdrop.
export const outside = (box, x, y) => x < box.left || x > box.right || y < box.top || y > box.bottom;

// Desk widths are bucketed in the shopper's unit (ns.gallery.size_unit): inches at 4, 5 and 6 ft, centimetres every 20.
export const SIZE_EDGES = { in: [48, 60, 72], cm: [100, 120, 140, 160] };
const UNIT = /(cm|mm|inches|inch|in\b|["″])/i;

// The width (the first number) of a setup's desk_size text, in `unit`, or null when the text has no number or no unit.
export function deskWidth(text, unit = 'cm') {
  const value = String(text ?? '');
  const number = /(\d+(?:\.\d+)?)/.exec(value);
  const found = UNIT.exec(value);
  if (!number || !found) return null;
  const n = Number(number[1]);
  const source = found[1].toLowerCase();
  const from = source === 'cm' ? 'cm' : source === 'mm' ? 'mm' : 'in';
  const cm = from === 'cm' ? n : from === 'mm' ? n / 10 : n * 2.54;
  if (!(cm >= 20 && cm <= 400)) return null;
  const width = unit === 'in' ? (from === 'in' ? n : cm / 2.54) : cm;
  return Math.round(width * 10) / 10;
}

export function sizeBucket(width, unit) {
  if (typeof width !== 'number' || !Number.isFinite(width)) return null;
  const edges = SIZE_EDGES[unit] ?? SIZE_EDGES.cm;
  const index = edges.findIndex(edge => width < edge);
  return index === -1 ? edges.length : index;
}

export function bucketLabel(index, unit, templates) {
  const edges = SIZE_EDGES[unit] ?? SIZE_EDGES.cm;
  if (index <= 0) return fill(templates.under, { max: edges[0] });
  if (index >= edges.length) return fill(templates.over, { min: edges.at(-1) });
  return fill(templates.range, { min: edges[index - 1], max: edges[index] });
}

// item: { products: 'a b', bucket }; filters: { product, size } where '' is any.
export function matches(item, filters) {
  const size = filters.size ?? '';
  return shows(item.products, filters.product ?? '') && (size === '' || String(item.bucket) === String(size));
}

const nullsLast = (a, b, order) => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return order * (a - b);
};
const SORTS = {
  featured: () => 0,
  items: (a, b) => b.count - a.count,
  wide: (a, b) => nullsLast(a.width, b.width, -1),
  narrow: (a, b) => nullsLast(a.width, b.width, 1),
};

// Featured is the merchant's order; the others break ties by it too.
export function sortItems(items, key) {
  const by = SORTS[key] ?? SORTS.featured;
  return [...items].sort((a, b) => by(a, b) || a.index - b.index);
}

// How many items each key appears on; pick(item) returns the item's keys (each counted once per item).
export function tally(items, pick) {
  const counts = new Map();
  for (const item of items) for (const key of new Set(pick(item))) counts.set(key, (counts.get(key) ?? 0) + 1);
  return counts;
}

export function selection(rows) {
  const picked = rows.filter(row => row.checked);
  return { count: picked.length, total: picked.reduce((sum, row) => sum + (Number(row.price) || 0), 0) };
}

// Look bundle (setup.bundle_discount_percent): the total less the percent, the cents off rounded half up as the panel's
// Liquid does; anything but a whole 1–99 leaves the total as it is. Shopify's automatic discount applies it at checkout.
export function bundleTotal(cents, percent) {
  const value = Number(cents) || 0;
  const share = Number(percent);
  if (!Number.isInteger(share) || share < 1 || share > 99) return value;
  return value - Math.round(value * share / 100);
}

// Like Shopify's money_without_trailing_zeros: $114, $114.50.
export function formatMoney(cents, currency) {
  const value = Number(cents);
  if (!Number.isFinite(value)) return '';
  const whole = value % 100 === 0;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(value / 100);
  } catch {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(value / 100);
  }
}

// ---- Browser wiring ----

const element = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

function startGallery(root) {
  const grid = root.querySelector('.ns-gallery__grid');
  if (!grid) return;
  const unit = root.dataset.sizeUnit === 'in' ? 'in' : 'cm';
  const templates = { under: root.dataset.sizeUnder, range: root.dataset.sizeRange, over: root.dataset.sizeOver };
  const items = [...grid.querySelectorAll('[data-ns-gallery-item]')].map(node => {
    const width = deskWidth(node.dataset.desk, unit);
    return { node, index: Number(node.dataset.index) || 0, products: node.dataset.products ?? '', count: Number(node.dataset.count) || 0, width, bucket: sizeBucket(width, unit) };
  });
  const state = { product: '', size: '', sort: 'featured' };

  // Product counts on the chips.
  const productCounts = tally(items, item => item.products.split(/\s+/).filter(Boolean));
  for (const chip of root.querySelectorAll('[data-ns-gallery-filter]')) {
    const slot = chip.querySelector('[data-ns-gallery-tally]');
    if (slot && chip.dataset.nsGalleryFilter) slot.textContent = String(productCounts.get(chip.dataset.nsGalleryFilter) ?? 0);
  }

  // Desk size chips, only for the buckets some setup is in.
  const sizes = root.querySelector('[data-ns-gallery-sizes]');
  const sizeList = root.querySelector('[data-ns-gallery-size-list]');
  const sizeCounts = tally(items, item => (item.bucket === null ? [] : [item.bucket]));
  if (sizes && sizeList && sizeCounts.size) {
    for (const index of [...sizeCounts.keys()].sort((a, b) => a - b)) {
      const chip = element('button', 'ns-chip-btn ns-gallery__chip');
      chip.type = 'button';
      chip.dataset.nsGallerySize = String(index);
      chip.setAttribute('aria-pressed', 'false');
      chip.append(element('span', 'ns-gallery__chip-name', bucketLabel(index, unit, templates)), element('span', 'ns-gallery__chip-count', String(sizeCounts.get(index))));
      const li = element('li');
      li.append(chip);
      sizeList.append(li);
    }
    sizes.hidden = false;
  }
  const sort = root.querySelector('[data-ns-gallery-sort]');
  if (sort && !sizeCounts.size) for (const option of sort.querySelectorAll('[data-needs-size]')) option.remove();

  const count = root.querySelector('[data-ns-gallery-count]');
  const active = root.querySelector('[data-ns-gallery-active]');
  const activeList = root.querySelector('[data-ns-gallery-active-list]');
  const none = root.querySelector('[data-ns-gallery-none]');
  const label = (selector, value) => [...root.querySelectorAll(selector)].find(node => (node.dataset.nsGalleryFilter ?? node.dataset.nsGallerySize) === value)?.querySelector('.ns-gallery__chip-name')?.textContent ?? value;

  const apply = () => {
    for (const chip of root.querySelectorAll('[data-ns-gallery-filter]')) chip.setAttribute('aria-pressed', String(chip.dataset.nsGalleryFilter === state.product));
    for (const chip of root.querySelectorAll('[data-ns-gallery-size]')) chip.setAttribute('aria-pressed', String(chip.dataset.nsGallerySize === state.size));
    let shown = 0;
    for (const item of sortItems(items, state.sort)) {
      item.node.hidden = !matches(item, state);
      if (!item.node.hidden) shown += 1;
      grid.append(item.node);
      for (const tag of item.node.querySelectorAll('.ns-gallery__tag[data-handle]')) tag.toggleAttribute('data-match', tag.dataset.handle === state.product);
    }
    if (count) count.textContent = fill(shown === 1 ? count.dataset.one : count.dataset.other, { count: shown });
    if (none) none.hidden = shown > 0;
    if (active && activeList) {
      const chosen = [];
      if (state.product) chosen.push(['product', label('[data-ns-gallery-filter]', state.product)]);
      if (state.size !== '') chosen.push(['size', label('[data-ns-gallery-size]', state.size)]);
      activeList.replaceChildren(...chosen.map(([kind, name]) => {
        const li = element('li');
        const button = element('button', 'ns-gallery__active-chip', name);
        button.type = 'button';
        button.dataset.nsGalleryRemove = kind;
        button.setAttribute('aria-label', fill(activeList.dataset.remove, { name }));
        button.append(element('span', 'ns-gallery__active-x', '✕'));
        button.lastChild.setAttribute('aria-hidden', 'true');
        li.append(button);
        return li;
      }));
      active.hidden = chosen.length === 0;
    }
  };

  // "+N more" opens the product row (at 750 px and up the chips past the first six or the merchant's picks wait behind it).
  const openChips = () => {
    const more = root.querySelector('[data-ns-gallery-more]');
    const list = more?.closest('ul');
    if (!list || list.hasAttribute('data-open')) return null;
    list.setAttribute('data-open', '');
    more.setAttribute('aria-expanded', 'true');
    return list.querySelector('[data-ns-gallery-extra] button');
  };

  root.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-ns-gallery-more]')) { openChips()?.focus(); return; }
    const product = target?.closest('[data-ns-gallery-filter]');
    if (product) { state.product = product.dataset.nsGalleryFilter; apply(); return; }
    const size = target?.closest('[data-ns-gallery-size]');
    if (size) { state.size = state.size === size.dataset.nsGallerySize ? '' : size.dataset.nsGallerySize; apply(); return; }
    const remove = target?.closest('[data-ns-gallery-remove]');
    if (remove) {
      state[remove.dataset.nsGalleryRemove] = '';
      apply();
      root.querySelector('[data-ns-gallery-filter][aria-pressed="true"]')?.focus();
      return;
    }
    if (target?.closest('[data-ns-gallery-clear]')) { state.product = ''; state.size = ''; apply(); }
  });
  sort?.addEventListener('change', () => { state.sort = sort.value; apply(); });

  const preset = filterFromSearch(window.location.search);
  const presetChip = preset ? root.querySelector(`[data-ns-gallery-filter="${preset}"]`) : null;
  if (presetChip) {
    state.product = preset;
    if (presetChip.closest('[data-ns-gallery-extra]')) openChips();
  }
  apply();
}

// The setup album (2026-10-23): which photo the snap track shows, and what the arrows may do there.
export const albumIndex = (scrollLeft, width, count) => (count > 0 && width > 0 ? Math.min(count - 1, Math.max(0, Math.round(Number(scrollLeft) / width))) : 0);
export const albumEnds = (index, count) => ({ first: index <= 0, last: index >= count - 1 });

function syncAlbum(album) {
  const track = album.querySelector('[data-ns-album-track]');
  if (!track) return;
  const slides = track.querySelectorAll('[data-ns-album-slide]').length;
  const index = albumIndex(track.scrollLeft, track.clientWidth, slides);
  if (album.dataset.nsAlbumAt === String(index)) return;
  album.dataset.nsAlbumAt = String(index);
  const ends = albumEnds(index, slides);
  const prev = album.querySelector('[data-ns-album-step="-1"]');
  const next = album.querySelector('[data-ns-album-step="1"]');
  if (prev) prev.disabled = ends.first;
  if (next) next.disabled = ends.last;
  const count = album.querySelector('[data-ns-album-count]');
  if (count) count.textContent = fill(count.dataset.template, { index: index + 1 });
  for (const thumb of album.querySelectorAll('[data-ns-album-thumb]')) {
    const on = Number(thumb.dataset.nsAlbumThumb) === index;
    if (on) { thumb.setAttribute('aria-current', 'true'); thumb.scrollIntoView?.({ block: 'nearest', inline: 'nearest' }); } else thumb.removeAttribute('aria-current');
  }
}

function showPhoto(album, index) {
  const track = album.querySelector('[data-ns-album-track]');
  if (!track) return;
  const slides = track.querySelectorAll('[data-ns-album-slide]').length;
  const to = Math.min(slides - 1, Math.max(0, index));
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  track.scrollTo({ left: to * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
}

// Shop the look on demand (home perf, 2026-10-23): a tile with data-ns-look-open="<setup handle>" opens the dialog
// NsLookD-<handle>; the dialogs come from one Section Rendering fetch of sections/ns-look-dialogs.liquid, made on the
// first hover, focus or click and kept for the page. Inside them ‹ › step through the tiles visible in the opener's section.
export const lookDialogId = handle => `NsLookD-${handle}`;
export const lookDialogsUrl = root => `${String(root || '/').replace(/\/?$/, '/')}?section_id=ns-look-dialogs`;
export const stepHandle = (handles, current, dir) => {
  const index = handles.indexOf(current);
  return index < 0 || handles.length < 2 ? null : handles[(index + dir + handles.length) % handles.length];
};
let lookDialogsLoad = null;
let lookSource = null;
function loadLookDialogs() {
  if (!lookDialogsLoad) {
    lookDialogsLoad = fetch(lookDialogsUrl(window.Shopify?.routes?.root), { credentials: 'same-origin' })
      .then(response => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then(html => {
        // Parsed, not written as markup: the response is this store's own section, and its nodes move into the page.
        const holder = document.createElement('div');
        holder.setAttribute('data-ns-look-holder', '');
        holder.append(...new DOMParser().parseFromString(html, 'text/html').body.childNodes);
        document.body.append(holder);
        for (const panel of holder.querySelectorAll('[data-ns-look]')) syncLook(panel);
        for (const album of holder.querySelectorAll('[data-ns-album]')) startAlbum(album);
        return holder;
      })
      .catch(error => { lookDialogsLoad = null; throw error; });
  }
  return lookDialogsLoad;
}
async function openLook(handle, opener) {
  let dialog = document.getElementById(lookDialogId(handle));
  if (!dialog) {
    opener?.setAttribute('aria-busy', 'true');
    try { await loadLookDialogs(); } catch { /* the tile stays as it is; the next click tries again */ } finally { opener?.removeAttribute('aria-busy'); }
    dialog = document.getElementById(lookDialogId(handle));
  }
  if (!dialog) return;
  if (opener) lookSource = opener.closest('.shopify-section') ?? lookSource;
  for (const open of document.querySelectorAll('dialog[open].ns-dialog')) open.close();
  dialog.showModal();
}
const visibleHandles = root => [...(root?.querySelectorAll('[data-ns-look-open]') ?? [])].filter(button => !button.closest('[hidden]') && button.offsetParent !== null).map(button => button.dataset.nsLookOpen);

// The home setup rail (2026-10-23): ‹ › scroll it by most of a screen and grey out at its ends.
export const railEnds = (scrollLeft, clientWidth, scrollWidth) => ({ first: scrollLeft <= 2, last: scrollLeft + clientWidth >= scrollWidth - 2 });
function startRail(rail) {
  const track = rail.querySelector('[data-ns-rail-track]');
  if (!track || rail.dataset.nsRailOn) return;
  rail.dataset.nsRailOn = '1';
  const sync = () => {
    const ends = railEnds(track.scrollLeft, track.clientWidth, track.scrollWidth);
    const prev = rail.querySelector('[data-ns-rail-step="-1"]');
    const next = rail.querySelector('[data-ns-rail-step="1"]');
    if (prev) prev.disabled = ends.first;
    if (next) next.disabled = ends.last;
  };
  let queued = false;
  track.addEventListener('scroll', () => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; sync(); }); }, { passive: true });
  sync();
}

function startAlbum(album) {
  const track = album.querySelector('[data-ns-album-track]');
  if (!track || album.dataset.nsAlbumOn) return;
  album.dataset.nsAlbumOn = '1';
  let queued = false;
  track.addEventListener('scroll', () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; syncAlbum(album); });
  }, { passive: true });
  track.addEventListener('keydown', event => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    showPhoto(album, Number(album.dataset.nsAlbumAt || 0) + (event.key === 'ArrowRight' ? 1 : -1));
  });
}

// ‹ › on a look dialog's photo (07 prototype): the previous or next look dialog in the same section, wrapping round.
export const stepIndex = (index, count, dir) => (count > 0 ? (index + dir + count) % count : 0);

function lookDialogs(dialog) {
  const scope = dialog.closest('.shopify-section') ?? document;
  return [...scope.querySelectorAll('dialog.ns-look')];
}

// A Shop the look panel: the count, the total (the bundle price and the struck list total when the look has a bundle
// percent) and the button follow the checked rows; Only in-stock hides the rest.
function syncLook(panel) {
  const form = panel.querySelector('[data-ns-look-form]');
  if (!form) return;
  const boxes = [...panel.querySelectorAll('input[type="checkbox"][form]')];
  const picked = selection(boxes.map(box => ({ checked: box.checked, price: box.dataset.price })));
  const selected = panel.querySelector('[data-ns-look-selected]');
  if (selected) selected.textContent = fill(selected.dataset.template, { count: picked.count, total: panel.querySelectorAll('[data-ns-look-item]').length });
  const percent = Number(form.dataset.percent) || 0;
  const total = panel.querySelector('[data-ns-look-total]');
  if (total) total.textContent = formatMoney(bundleTotal(picked.total, percent), form.dataset.currency);
  const was = panel.querySelector('[data-ns-look-was]');
  if (was) { was.textContent = formatMoney(picked.total, form.dataset.currency); was.hidden = picked.count === 0; }
  const add = panel.querySelector('[data-ns-look-add]');
  if (add) {
    add.textContent = fill(add.dataset.template, { count: picked.count });
    add.disabled = picked.count === 0;
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.matches('dialog.ns-look') && outside(target.getBoundingClientRect(), event.clientX, event.clientY)) { target.close(); return; }
    const lookOpen = target?.closest('[data-ns-look-open]');
    if (lookOpen) { openLook(lookOpen.dataset.nsLookOpen, lookOpen); return; }
    const railStep = target?.closest('[data-ns-rail-step]');
    if (railStep) {
      const track = railStep.closest('[data-ns-rail]')?.querySelector('[data-ns-rail-track]');
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      track?.scrollBy({ left: Number(railStep.dataset.nsRailStep) * track.clientWidth * 0.9, behavior: reduce ? 'auto' : 'smooth' });
      return;
    }
    const photoStep = target?.closest('[data-ns-album-step]');
    const thumb = target?.closest('[data-ns-album-thumb]');
    if (photoStep || thumb) {
      const album = (photoStep || thumb).closest('[data-ns-album]');
      if (album) showPhoto(album, thumb ? Number(thumb.dataset.nsAlbumThumb) : Number(album.dataset.nsAlbumAt || 0) + Number(photoStep.dataset.nsAlbumStep));
      return;
    }
    const step = target?.closest('[data-ns-look-step]');
    const held = step?.closest('[data-ns-look-holder] dialog.ns-look');
    if (step && held) {
      const next = stepHandle(visibleHandles(lookSource), held.id.replace(/^NsLookD-/, ''), Number(step.dataset.nsLookStep));
      if (next) openLook(next, null);
      return;
    }
    if (step) {
      const current = step.closest('dialog.ns-look');
      const all = current ? lookDialogs(current) : [];
      const next = all[stepIndex(all.indexOf(current), all.length, Number(step.dataset.nsLookStep))];
      if (next && next !== current) { current.close(); next.showModal(); }
      return;
    }
    const toggle = target?.closest('[data-ns-look-instock]');
    if (toggle) {
      const on = toggle.getAttribute('aria-pressed') !== 'true';
      toggle.setAttribute('aria-pressed', String(on));
      for (const row of toggle.closest('[data-ns-look]')?.querySelectorAll('[data-ns-look-item][data-stock="out"]') ?? []) row.hidden = on;
    }
  });
  document.addEventListener('change', event => {
    const box = event.target instanceof Element ? event.target.closest('[data-ns-look] input[type="checkbox"][form]') : null;
    if (box) syncLook(box.closest('[data-ns-look]'));
  });
  // The add runs in the cart (ns-cart.js); a popup closes so its toast and drawer are not behind it.
  document.addEventListener('submit', event => {
    const form = event.target instanceof Element ? event.target.closest('[data-ns-look-form]') : null;
    form?.closest('dialog.ns-look[open]')?.close();
  });
  // Warm the dialogs before the click: the first pointer over, or focus on, a deferred tile starts the fetch.
  const warm = event => { if (event.target instanceof Element && event.target.closest('[data-ns-look-open]')) loadLookDialogs().catch(() => {}); };
  document.addEventListener('pointerover', warm, { passive: true });
  document.addEventListener('focusin', warm);
  const start = () => {
    for (const root of document.querySelectorAll('[data-ns-gallery]')) startGallery(root);
    for (const panel of document.querySelectorAll('[data-ns-look]')) syncLook(panel);
    for (const album of document.querySelectorAll('[data-ns-album]')) startAlbum(album);
    for (const rail of document.querySelectorAll('[data-ns-rail]')) startRail(rail);
    // A section with one look has nowhere to step to.
    for (const dialog of document.querySelectorAll('dialog.ns-look')) if (lookDialogs(dialog).length < 2) dialog.querySelector('.ns-look__step')?.setAttribute('hidden', '');
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}
